import { Link } from 'react-router-dom';
import { useTiltEffect } from '@/hooks/useTiltEffect';

export default function HeroSection() {
  const tilt = useTiltEffect({ maxTilt: 8, scale: 1.03, speed: 600 });

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-black">
      {/* Luxury gold background image */}
      <img
        src="https://readdy.ai/api/search-image?query=Elegant%20dark%20backdrop%20with%20scattered%20golden%20geometric%20polygon%20shards%20and%20crystalline%20facets%20reflecting%20warm%20champagne%20light%2C%20subtle%20marble%20veining%20in%20muted%20gold%20tones%20across%20black%20surface%2C%20refined%20luxury%20banking%20hall%20atmosphere%2C%20soft%20radial%20bokeh%20of%20golden%20highlights%2C%20sophisticated%20dark%20premium%20aesthetic%20with%20layered%20depth%20and%20dimension%2C%20no%20text%20no%20logos%2C%20photorealistic%208k%20render&width=1920&height=1080&seq=hero-luxury-gold&orientation=landscape&nocache=true"
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Dark overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />

      {/* Gold geometric lines */}
      <div className="absolute inset-0 opacity-[0.06]">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary-400 to-transparent" />
        <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary-400/60 to-transparent" />
        <div className="absolute bottom-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary-400/40 to-transparent" />
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-primary-400/30 to-transparent" />
        <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-primary-400/20 to-transparent" />
        {/* Corner accents */}
        <div className="absolute top-12 left-12 w-16 h-16 border-t border-l border-primary-400/25" />
        <div className="absolute top-12 right-12 w-16 h-16 border-t border-r border-primary-400/25" />
        <div className="absolute bottom-12 left-12 w-16 h-16 border-b border-l border-primary-400/25" />
        <div className="absolute bottom-12 right-12 w-16 h-16 border-b border-r border-primary-400/25" />
      </div>

      {/* Gold glow behind content */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] bg-gradient-radial from-primary-500/12 via-primary-600/5 to-transparent rounded-full blur-3xl" />

      <div className="relative w-full px-4 md:px-8 lg:px-12 pt-24 md:pt-32 pb-20 md:pb-0">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-8">
          {/* Left Content */}
          <div className="w-full lg:w-1/2 flex flex-col items-start relative">
            <div className="hero-glow -top-10 -left-10" />
            <h1 className="relative font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1] mb-6">
              Trusted Money
              <br />
              <span className="text-primary-400">Transfer</span>
            </h1>

            <p className="font-body text-foreground-200 text-base md:text-lg leading-relaxed max-w-xl mb-8">
              A next-generation digital wallet and payment ecosystem, fully compliant with central banking regulatory standards across Europe, the Middle East, and Africa. Send money, pay bills, shop online, and manage your entire financial life — all from one secure, intelligent platform.
            </p>

            <div className="flex flex-col sm:flex-row items-start gap-4">
              <Link
                to="/signup"
                className="group flex items-center"
              >
                <span className="px-6 py-3.5 bg-primary-500 text-black font-label text-sm font-semibold tracking-wider uppercase rounded-l-md whitespace-nowrap hover:bg-primary-400 transition-colors">
                  Open Your Account
                </span>
                <span className="w-12 h-12 flex items-center justify-center border border-primary-500 rounded-r-md group-hover:bg-primary-500/20 transition-colors">
                  <i className="ri-arrow-right-line text-primary-400 text-lg" />
                </span>
              </Link>
              <Link
                to="/services"
                className="px-6 py-3.5 border border-primary-600/50 text-white font-label text-sm tracking-wider uppercase rounded-md hover:border-primary-400 hover:text-primary-400 transition-colors whitespace-nowrap"
              >
                Explore Services
              </Link>
            </div>

            {/* Trust badges */}
            <div className="flex items-center gap-6 mt-10">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-shield-check-line text-primary-500 text-xl" />
                </div>
                <span className="text-foreground-300 text-xs font-body">256-bit Encryption</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-verified-badge-line text-primary-500 text-xl" />
                </div>
                <span className="text-foreground-300 text-xs font-body">Fully Licensed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-24-hours-line text-primary-500 text-xl" />
                </div>
                <span className="text-foreground-300 text-xs font-body">24/7 Support</span>
              </div>
            </div>
          </div>

          {/* Right Visual */}
          <div className="w-full lg:w-1/2 flex justify-center lg:justify-end">
            <div
              ref={tilt.ref}
              onMouseMove={tilt.onMouseMove}
              onMouseLeave={tilt.onMouseLeave}
              style={tilt.style}
              className="relative w-full max-w-lg aspect-square cursor-default"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary-700/20 via-transparent to-primary-900/20 rounded-full blur-3xl" />
              <img
                src="https://readdy.ai/api/search-image?query=Premium%20golden%20fintech%20app%20interface%20floating%20on%20dark%20background%2C%20sleek%20minimal%20digital%20wallet%20design%20with%20champagne%20gold%20accents%2C%20glass%20morphism%20cards%2C%20soft%20golden%20glow%20highlights%2C%20luxury%20dark%20mode%20UI%20concept%2C%20elegant%20modern%20aesthetic%2C%20high%20contrast%20gold%20on%20black%2C%20no%20text&width=800&height=800&seq=hero-visual&orientation=squarish&nocache=true"
                alt="TMT Digital Wallet"
                className="relative w-full h-full object-cover rounded-2xl"
              />
              {/* Floating stats card */}
              <div className="absolute -bottom-4 -left-4 md:bottom-8 md:-left-8 bg-black/80 backdrop-blur-md border border-primary-700/30 rounded-xl p-4 shadow-xl animate-float-slow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center">
                    <i className="ri-arrow-up-circle-line text-primary-400 text-lg" />
                  </div>
                  <div>
                    <p className="font-heading text-lg font-bold text-primary-400">$2.5B+</p>
                    <p className="text-foreground-500 text-xs font-body">Transactions</p>
                  </div>
                </div>
              </div>
              {/* Floating users card */}
              <div className="absolute -top-4 -right-4 md:top-8 md:-right-8 bg-black/80 backdrop-blur-md border border-primary-700/30 rounded-xl p-4 shadow-xl animate-float-slow" style={{ animationDelay: '1.5s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center">
                    <i className="ri-user-add-line text-primary-400 text-lg" />
                  </div>
                  <div>
                    <p className="font-heading text-lg font-bold text-primary-400">500K+</p>
                    <p className="text-foreground-500 text-xs font-body">Active Users</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}